import React, { useEffect, useMemo, useState } from "react";
import { Bell, BellOff, Plus, Trash2, Check, CalendarDays, ExternalLink, Video, X, Loader2, MapPin, Users as UsersIcon } from "lucide-react";
import { useReminders } from "@/context/RemindersContext";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { formatDate, relTime } from "@/lib/utils-fos";

// Calendar page — Google Calendar iframe embed + local reminders + local meetings.
export default function Calendar() {
  const { reminders, meetings, refresh, markDone, deleteReminder, notifPermission, requestNotifPermission } = useReminders();
  const [gcalEmail, setGcalEmail] = useState("");
  const [gcalStored, setGcalStored] = useState("");
  const [settingsId, setSettingsId] = useState(null);
  const [modal, setModal] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);   // Google Calendar events via Nango
  const [liveConnected, setLiveConnected] = useState(false);

  const loadLiveEvents = async () => {
    try {
      const r = await endpoints.nango.calendarEvents();
      setLiveEvents(r.events || []);
      setLiveConnected(true);
    } catch (e) {
      // 409 = calendar not connected via Nango — that's fine, iframe fallback works.
      setLiveConnected(false);
      setLiveEvents([]);
    }
  };

  useEffect(() => {
    endpoints.settings().then((rows) => {
      const e = rows.find((r) => r.key === "google_calendar_email");
      if (e) { setGcalEmail(e.value); setGcalStored(e.value); setSettingsId(e.id); }
    });
    loadLiveEvents();
  }, []);

  const saveGcalEmail = async () => {
    if (settingsId) await endpoints.updateSetting(settingsId, { value: gcalEmail });
    else {
      const s = await endpoints.createSetting({ key: "google_calendar_email", value: gcalEmail });
      setSettingsId(s.id);
    }
    setGcalStored(gcalEmail);
    toast.success("Google Calendar linked");
  };

  const iframeSrc = useMemo(() => {
    if (!gcalStored) return null;
    const src = encodeURIComponent(gcalStored);
    return `https://calendar.google.com/calendar/embed?src=${src}&ctz=UTC&mode=WEEK&showTitle=0&showPrint=0&showTabs=1&showCalendars=0`;
  }, [gcalStored]);

  const now = Date.now();
  const upcomingReminders = reminders
    .filter((r) => !r.completed && Date.parse(r.remind_at) > now - 60000)
    .sort((a, b) => Date.parse(a.remind_at) - Date.parse(b.remind_at));
  const overdueReminders = reminders
    .filter((r) => !r.completed && Date.parse(r.remind_at) <= now - 60000);
  const upcomingMeetings = meetings
    .filter((m) => Date.parse(m.start_at) > now - 60000)
    .sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] pb-16 md:pb-0">
      <div className="p-4 md:p-6 border-b border-neutral-200 bg-white flex items-center justify-between gap-3 flex-wrap shrink-0">
        <div>
          <h1 className="text-lg md:text-xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-xs text-neutral-500">Google Calendar meetings · local reminders</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {notifPermission !== "granted" && (
            <button
              onClick={async () => { const p = await requestNotifPermission(); if (p === "granted") toast.success("Browser notifications enabled"); }}
              className="text-xs px-3 py-1.5 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center gap-1.5 text-neutral-700"
              data-testid="calendar-enable-notif"
            >
              {notifPermission === "denied" ? <BellOff size={12} /> : <Bell size={12} />}
              {notifPermission === "denied" ? "Notifications blocked" : "Enable notifications"}
            </button>
          )}
          <button
            onClick={() => setModal({ kind: "reminder" })}
            className="text-xs px-3 py-1.5 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 flex items-center gap-1.5"
            data-testid="calendar-new-reminder"
          >
            <Bell size={12} /> New reminder
          </button>
          <button
            onClick={() => setModal({ kind: "meeting" })}
            className="text-xs px-3 py-1.5 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50 flex items-center gap-1.5 text-neutral-700"
            data-testid="calendar-new-meeting"
          >
            <CalendarDays size={12} /> New meeting
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <aside className="w-full lg:w-96 shrink-0 lg:border-r border-neutral-200 bg-white flex flex-col overflow-hidden">
          <div className="overflow-y-auto flex-1 p-4 space-y-6">
            {overdueReminders.length > 0 && (
              <Section title="Overdue" count={overdueReminders.length} tone="danger">
                {overdueReminders.map((r) => (
                  <ReminderCard key={r.id} reminder={r} onDone={() => markDone(r.id)} onDelete={() => deleteReminder(r.id)} overdue />
                ))}
              </Section>
            )}
            <Section title="Upcoming reminders" count={upcomingReminders.length}>
              {upcomingReminders.length === 0 && <EmptyState label="No reminders scheduled." />}
              {upcomingReminders.map((r) => (
                <ReminderCard key={r.id} reminder={r} onDone={() => markDone(r.id)} onDelete={() => deleteReminder(r.id)} />
              ))}
            </Section>
            <Section title="Upcoming meetings" count={upcomingMeetings.length}>
              {upcomingMeetings.length === 0 && <EmptyState label="No meetings scheduled." />}
              {upcomingMeetings.map((m) => (
                <MeetingCard key={m.id} meeting={m} refresh={refresh} />
              ))}
            </Section>
            {liveConnected && (
              <Section title="Google Calendar · live" count={liveEvents.length}>
                {liveEvents.length === 0 && <EmptyState label="Nothing on your calendar in the next 14 days." />}
                {liveEvents.map((e) => (
                  <LiveEventCard key={e.id} event={e} />
                ))}
              </Section>
            )}
          </div>
        </aside>

        <main className="flex-1 min-w-0 flex flex-col overflow-hidden bg-[#FAFAFA]">
          {!gcalStored ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="w-full max-w-md fos-card p-5">
                <CalendarDays size={22} className="text-neutral-500 mb-2" />
                <h2 className="text-sm font-semibold mb-1">Connect your Google Calendar</h2>
                <p className="text-xs text-neutral-500 mb-3">Paste your calendar's public / shared email (usually the same as your Google account). We embed a read-only view of it here — no OAuth needed. Meetings you schedule from Ask will still open a Google Calendar create-event link.</p>
                <input
                  value={gcalEmail}
                  onChange={(e) => setGcalEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-2"
                  data-testid="calendar-email-input"
                />
                <button
                  onClick={saveGcalEmail}
                  disabled={!gcalEmail.trim()}
                  className="text-sm w-full px-3 py-2 rounded-sm bg-neutral-900 text-white disabled:opacity-40"
                  data-testid="calendar-email-save"
                >
                  Connect
                </button>
                <p className="text-[10px] text-neutral-400 mt-2">
                  In Google Calendar → Settings → Access permissions, tick "Make available to public" (or share with anyone-with-link) so the embed can render your events.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 bg-white text-xs text-neutral-500">
                <span>Showing</span>
                <span className="text-neutral-900 font-medium">{gcalStored}</span>
                <button
                  onClick={() => { setGcalStored(""); setGcalEmail(""); }}
                  className="ml-auto text-neutral-500 hover:text-neutral-900"
                  data-testid="calendar-change-email"
                >Change</button>
                <a href={`https://calendar.google.com/calendar/u/0/r`} target="_blank" rel="noreferrer" className="ml-2 flex items-center gap-1 text-neutral-500 hover:text-neutral-900">
                  Open in Google <ExternalLink size={11} />
                </a>
              </div>
              <iframe
                title="Google Calendar"
                src={iframeSrc}
                className="flex-1 w-full border-0"
                data-testid="calendar-iframe"
              />
            </>
          )}
        </main>
      </div>

      {modal?.kind === "reminder" && (
        <ReminderModal onClose={() => setModal(null)} onCreated={() => { setModal(null); refresh(); }} />
      )}
      {modal?.kind === "meeting" && (
        <MeetingModal onClose={() => setModal(null)} onCreated={() => { setModal(null); refresh(); loadLiveEvents(); }} liveConnected={liveConnected} />
      )}
    </div>
  );
}

function LiveEventCard({ event }) {
  const start = event.start_at ? new Date(event.start_at) : null;
  const abs = start ? start.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="group p-2.5 rounded-md border border-emerald-100 bg-emerald-50/30" data-testid={`gcal-event-${event.id}`}>
      <div className="flex items-start gap-2">
        <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-900 leading-snug truncate">{event.summary}</p>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            {abs}{event.all_day ? " · all-day" : ""}
          </p>
          {event.location && (
            <p className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1"><MapPin size={9} /> {event.location}</p>
          )}
          {event.attendees?.length > 0 && (
            <p className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1">
              <UsersIcon size={9} /> {event.attendees.slice(0, 3).map((a) => a.email).join(", ")}
              {event.attendees.length > 3 && ` +${event.attendees.length - 3}`}
            </p>
          )}
        </div>
        {event.html_link && (
          <a href={event.html_link} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100 text-neutral-600" title="Open in Google Calendar">
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, tone, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] font-medium uppercase tracking-wider ${tone === "danger" ? "text-[#FF453A]" : "text-neutral-500"}`}>{title}</p>
        <span className="text-[10px] text-neutral-500">{count}</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function EmptyState({ label }) {
  return <p className="text-[11px] italic text-neutral-400 py-2">{label}</p>;
}

function ReminderCard({ reminder, onDone, onDelete, overdue }) {
  const t = Date.parse(reminder.remind_at);
  const abs = new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  return (
    <div
      className={`group flex items-start gap-2 p-2.5 rounded-md border ${overdue ? "border-red-200 bg-red-50/40" : "border-neutral-200 bg-white"}`}
      data-testid={`reminder-${reminder.id}`}
    >
      <button
        onClick={onDone}
        className="mt-0.5 h-4 w-4 rounded border border-neutral-300 hover:bg-neutral-900 hover:border-neutral-900 hover:text-white flex items-center justify-center shrink-0"
        title="Mark done"
        data-testid={`reminder-done-${reminder.id}`}
      >
        <Check size={10} className="opacity-0 hover:opacity-100" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-neutral-900 leading-snug">{reminder.title}</p>
        {reminder.notes && <p className="text-[11px] text-neutral-600 mt-0.5">{reminder.notes}</p>}
        <p className={`text-[11px] mt-1 ${overdue ? "text-[#FF453A]" : "text-neutral-500"}`}>
          {abs} · {relTime(reminder.remind_at)}
        </p>
      </div>
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 p-1 shrink-0"
        title="Delete"
        data-testid={`reminder-delete-${reminder.id}`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

function MeetingCard({ meeting, refresh }) {
  const start = new Date(meeting.start_at);
  const end = meeting.end_at ? new Date(meeting.end_at) : null;
  const abs = start.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const del = async () => {
    if (!confirm("Delete this meeting?")) return;
    await endpoints.deleteMeeting(meeting.id);
    refresh();
  };
  return (
    <div className="group p-2.5 rounded-md border border-neutral-200 bg-white" data-testid={`meeting-${meeting.id}`}>
      <div className="flex items-start gap-2">
        <Video size={13} className="text-neutral-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-900 leading-snug">{meeting.title}</p>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            {abs}{end && ` · ${meeting.duration_min}m`}
          </p>
          {meeting.attendees?.length > 0 && (
            <p className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1"><UsersIcon size={9} /> {meeting.attendees.slice(0, 3).join(", ")}{meeting.attendees.length > 3 && ` +${meeting.attendees.length - 3}`}</p>
          )}
          {meeting.location && (
            <p className="text-[10px] text-neutral-500 mt-0.5 flex items-center gap-1"><MapPin size={9} /> {meeting.location}</p>
          )}
        </div>
        <button
          onClick={del}
          className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 p-1 shrink-0"
          title="Delete"
        >
          <Trash2 size={11} />
        </button>
      </div>
      {meeting.google_calendar_link && (
        <a
          href={meeting.google_calendar_link}
          target="_blank"
          rel="noreferrer"
          className="mt-2 text-[11px] inline-flex items-center gap-1 text-neutral-700 hover:text-neutral-900 underline decoration-neutral-300"
          data-testid={`meeting-gcal-link-${meeting.id}`}
        >
          Add to Google Calendar <ExternalLink size={9} />
        </a>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Modals
// -----------------------------------------------------------------------------
function ReminderModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("relative"); // relative | absolute
  const [seconds, setSeconds] = useState(0);
  const [minutes, setMinutes] = useState(15);
  const [hours, setHours] = useState(0);
  const [absDate, setAbsDate] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16); // yyyy-mm-ddThh:mm
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      let remind_at;
      if (mode === "relative") {
        const offsetMs = (Number(seconds) * 1000) + (Number(minutes) * 60000) + (Number(hours) * 3600000);
        if (offsetMs <= 0) { toast.error("Set a non-zero offset"); setSaving(false); return; }
        remind_at = new Date(Date.now() + offsetMs).toISOString();
      } else {
        remind_at = new Date(absDate).toISOString();
      }
      await endpoints.createReminder({ title, notes, remind_at });
      toast.success("Reminder set");
      onCreated();
    } catch (e) { toast.error("Could not create reminder"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="modal-new-reminder">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">New reminder</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Remind me to…"
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-2"
          data-testid="reminder-title" />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes…"
          rows={2}
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-3"
          data-testid="reminder-notes" />
        <div className="flex items-center gap-2 mb-2">
          <button type="button" onClick={() => setMode("relative")} data-active={mode === "relative"} className="chip" data-testid="reminder-mode-relative">In…</button>
          <button type="button" onClick={() => setMode("absolute")} data-active={mode === "absolute"} className="chip" data-testid="reminder-mode-absolute">At specific time</button>
        </div>
        {mode === "relative" ? (
          <div className="grid grid-cols-3 gap-2 mb-3">
            <NumberInput label="Hours" value={hours} onChange={setHours} testid="reminder-hours" />
            <NumberInput label="Minutes" value={minutes} onChange={setMinutes} testid="reminder-minutes" />
            <NumberInput label="Seconds" value={seconds} onChange={setSeconds} testid="reminder-seconds" />
          </div>
        ) : (
          <input type="datetime-local" value={absDate} onChange={(e) => setAbsDate(e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-3"
            data-testid="reminder-absdate" />
        )}
        <button onClick={submit} disabled={saving || !title.trim()}
          className="w-full text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="reminder-submit">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />} Set reminder
        </button>
      </div>
    </div>
  );
}

function MeetingModal({ onClose, onCreated, liveConnected }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startAt, setStartAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [duration, setDuration] = useState(30);
  const [attendees, setAttendees] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const start = new Date(startAt);
      const end = new Date(start.getTime() + duration * 60000);
      const attList = attendees.split(",").map((s) => s.trim()).filter(Boolean);
      // Compose Google Calendar deep link
      const fmt = (d) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");
      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: title,
        dates: `${fmt(start)}/${fmt(end)}`,
      });
      if (description) params.set("details", description);
      if (location) params.set("location", location);
      if (attList.length) params.set("add", attList.join(","));
      const gcalLink = "https://calendar.google.com/calendar/u/0/r/eventedit?" + params.toString();

      await endpoints.createMeeting({
        title, description,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        duration_min: Number(duration),
        attendees: attList,
        location,
        google_calendar_link: gcalLink,
      });
      // If Google Calendar is connected via Nango, ALSO create a real event.
      if (liveConnected) {
        try {
          await endpoints.nango.createCalendarEvent({
            summary: title,
            description,
            start_at: start.toISOString(),
            end_at:   end.toISOString(),
            attendees: attList,
            location: location || null,
          });
          toast.success("Meeting scheduled on Google Calendar");
        } catch (err) {
          // Local meeting saved regardless — flag the sync failure gently.
          toast.error("Saved locally but Google Calendar sync failed");
        }
      } else {
        toast.success("Meeting scheduled", { action: { label: "Add to Google Calendar", onClick: () => window.open(gcalLink, "_blank") } });
      }
      onCreated();
    } catch (e) { toast.error("Could not schedule"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="modal-new-meeting">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">New meeting</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting title"
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-2"
          data-testid="meeting-title" />
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <p className="text-[10px] text-neutral-500 mb-1">Start</p>
            <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
              className="w-full text-sm px-2 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500"
              data-testid="meeting-start" />
          </div>
          <NumberInput label="Duration (min)" value={duration} onChange={setDuration} testid="meeting-duration" />
        </div>
        <input value={attendees} onChange={(e) => setAttendees(e.target.value)} placeholder="Attendee emails (comma-separated)"
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-2"
          data-testid="meeting-attendees" />
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location or link (optional)"
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-2"
          data-testid="meeting-location" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Agenda / description…" rows={2}
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500 mb-3"
          data-testid="meeting-description" />
        <button onClick={submit} disabled={saving || !title.trim()}
          className="w-full text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40 flex items-center justify-center gap-2"
          data-testid="meeting-submit">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <CalendarDays size={13} />} Schedule
        </button>
        <p className="text-[10px] text-neutral-400 mt-2 text-center">Also generates a Google Calendar add-link.</p>
      </div>
    </div>
  );
}

function NumberInput({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-[10px] text-neutral-500 mb-1">{label}</p>
      <input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm px-2 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500"
        data-testid={testid} />
    </div>
  );
}
